import { ExternalLink, ShieldCheck, BellPlus } from "lucide-react";
import { safeHref } from "../lib/safeUrl";
import { Link } from "react-router-dom";

/**
 * Auto-renders the right provincial / territorial dental-hygiene regulator
 * based on `user.province`. Designed to drop straight into the member
 * Dashboard. Falls back gracefully when province is missing or unrecognised.
 */
const REGULATORS = {
  AB: { name: "Alberta College of Dental Hygienists", short: "ACDH", url: "https://acdh.ca" },
  BC: { name: "BC College of Oral Health Professionals", short: "BCCOHP", url: "https://www.bccohp.ca" },
  SK: { name: "College of Dental Hygienists of Saskatchewan", short: "CDHS", url: "https://www.cdhs.ca" },
  MB: { name: "College of Dental Hygienists of Manitoba", short: "CDHM", url: "https://www.cdhm.info" },
  ON: { name: "College of Dental Hygienists of Ontario", short: "CDHO", url: "https://www.cdho.org" },
  QC: { name: "Ordre des hygiénistes dentaires du Québec", short: "OHDQ", url: "https://www.ohdq.com" },
  NB: { name: "New Brunswick College of Dental Hygienists", short: "NBCDH", url: "https://www.nbcdh.ca" },
  NS: { name: "Nova Scotia College of DHs / DTs / DAs", short: "NSRDHDTD", url: "https://www.nsrdhdtd.ca" },
  PE: { name: "College of Dental Hygienists of PEI", short: "CDHPEI", url: "https://www.cdhpei.ca" },
  NL: { name: "Newfoundland & Labrador College of Dental Hygienists", short: "NLCDH", url: "https://www.nlcdh.com" },
  YT: { name: "Yukon Department of Community Services", short: "Gov of YT", url: "https://yukon.ca/en/community-services" },
  NT: { name: "NWT Department of Health and Social Services", short: "Gov of NWT", url: "https://www.hss.gov.nt.ca" },
  NU: { name: "Nunavut Professional Practice Unit", short: "Gov of NU", url: "https://www.gov.nu.ca/health" },
};

const PROVINCE_LABELS = {
  AB: "Alberta", BC: "British Columbia", SK: "Saskatchewan", MB: "Manitoba",
  ON: "Ontario", QC: "Quebec", NB: "New Brunswick", NS: "Nova Scotia",
  PE: "Prince Edward Island", NL: "Newfoundland & Labrador",
  YT: "Yukon", NT: "Northwest Territories", NU: "Nunavut",
};

export const MyRegulatorCard = ({ user }) => {
  const code = (user?.province || "").trim().toUpperCase();
  const reg = REGULATORS[code];

  if (!reg) {
    // No / unknown province — guide the user to set it on their profile.
    return (
      <div
        className="relative overflow-hidden rounded-2xl border border-[#E5DFEC] bg-gradient-to-br from-[#F0ECF7] to-[#FAF7F2] p-5"
        data-testid="my-regulator-card-empty"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-white/70 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-[#6B4E8A]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#6B4E8A]" style={{ fontFamily: "Outfit, sans-serif" }}>
              Set your province
            </p>
            <p className="text-xs text-[#64748B] mt-1 leading-relaxed">
              Add your province to your profile and we'll show your regulator's standards of practice and renewal info right here.
            </p>
            <Link
              to="/profile"
              className="inline-flex items-center gap-1 text-xs text-[#6B4E8A] hover:underline mt-2 font-medium"
              data-testid="my-regulator-go-profile"
            >
              Update profile <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[#E5DFEC] bg-gradient-to-br from-[#F0ECF7] via-white to-[#FAF7F2] p-5"
      data-testid={`my-regulator-card-${code}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-[#6B4E8A] flex items-center justify-center shrink-0">
          <ShieldCheck className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <p className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-medium">
              Your regulator · {PROVINCE_LABELS[code]}
            </p>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#8DB5A0]/15 text-[#8DB5A0] font-medium">
              {reg.short}
            </span>
          </div>
          <p
            className="text-base font-medium text-[#6B4E8A] leading-snug"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {reg.name}
          </p>
          <p className="text-xs text-[#64748B] mt-1 leading-relaxed">
            Standards of practice, registration, and continuing-competence requirements for {PROVINCE_LABELS[code]} RDHs.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <a
              href={safeHref(reg.url)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] transition-colors"
              data-testid={`my-regulator-visit-${code}`}
            >
              Visit site <ExternalLink className="w-3 h-3" />
            </a>
            <Link
              to="/ce-tracker"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-white border border-[#E5DFEC] text-[#6B4E8A] hover:border-[#6B4E8A] transition-colors"
              data-testid="my-regulator-ce-link"
              title="Track your continuing-competence credits in the CE Tracker"
            >
              <BellPlus className="w-3 h-3" /> Set CE renewal reminder
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyRegulatorCard;
